/**
 * Dashboard resolution: vault files → validated widget instances.
 *
 * Failure policy: a single broken widget config must never take down the
 * dashboard. Every widget resolves to either `{ ok: true, config }` or
 * `{ ok: false, error, path }`, and the UI renders the failure inside that
 * widget's own tile. Because these files are edited by hand and by AI, a
 * white-screen-on-typo would make the whole system feel hostile.
 */

import type { ZodType } from 'zod';
import { z } from 'zod';
import type { VaultFS } from './ports.js';
import { VaultNotFoundError } from './ports.js';
import { parseFile } from './markdown.js';
import {
  DashboardSchema,
  HomeSpanSchema,
  WidgetProbeSchema,
  DEFAULT_HOME_HEIGHT,
  formatIssues,
  type Dashboard,
  type Placement,
} from './schema.js';
import { DEFAULT_DASHBOARD, dashboardPath, widgetIdFromPath, widgetPath } from './paths.js';

/** Lookup from widget `type` to its config schema. Supplied by the shell's registry. */
export interface WidgetSchemaLookup {
  get(type: string): ZodType | undefined;
  types(): string[];
}

export type ResolvedWidget =
  | { ok: true; id: string; type: string; placement: Placement; path: string; config: unknown }
  | { ok: false; id: string; type?: string; placement: Placement; path: string; error: string };

export interface LoadedDashboard {
  name: string;
  path: string;
  dashboard: Dashboard;
  widgets: ResolvedWidget[];
  /** Set when the dashboard file itself is missing or invalid; `dashboard` is then a safe default. */
  error?: string;
}

const EMPTY_DASHBOARD: Dashboard = DashboardSchema.parse({});

export async function loadDashboard(
  fs: VaultFS,
  name: string,
  schemas: WidgetSchemaLookup,
): Promise<LoadedDashboard> {
  const path = dashboardPath(name);

  let raw: string;
  try {
    raw = (await fs.read(path)).content;
  } catch (err) {
    const message =
      err instanceof VaultNotFoundError
        ? `Dashboard not found: ${path}`
        : `Could not read ${path}: ${errorMessage(err)}`;
    return { name, path, dashboard: EMPTY_DASHBOARD, widgets: [], error: message };
  }

  const parsed = parseFile(raw);
  if (parsed.error) {
    return {
      name,
      path,
      dashboard: EMPTY_DASHBOARD,
      widgets: [],
      error: `Invalid YAML in ${path}: ${parsed.error}`,
    };
  }

  const result = DashboardSchema.safeParse(parsed.data);
  if (!result.success) {
    return {
      name,
      path,
      dashboard: EMPTY_DASHBOARD,
      widgets: [],
      error: `Invalid dashboard config in ${path} — ${formatIssues(result.error)}`,
    };
  }

  const dashboard = result.data;
  const layout =
    name === DEFAULT_DASHBOARD
      ? [...dashboard.layout, ...(await homePlacements(fs, dashboard))]
      : dashboard.layout;

  const widgets = await Promise.all(
    layout.map((placement) => resolveWidget(fs, placement, schemas)),
  );

  return { name, path, dashboard, widgets };
}

/** Just enough of a widget config to decide whether it pins itself to Home. */
const HomePinSchema = z.object({
  showOnHome: z.boolean().default(false),
  homeSpan: HomeSpanSchema.optional(),
});

/**
 * Widgets that opted onto Home with `showOnHome`, stacked below the explicit
 * layout in id order.
 *
 * Scanning every widget file looks expensive but isn't: adapters cache blobs by
 * sha and the store has already listed the vault, so these reads are local.
 *
 * Anything already placed by hand is skipped, so the flag and an explicit entry
 * can coexist without rendering the widget twice.
 */
async function homePlacements(fs: VaultFS, dashboard: Dashboard): Promise<Placement[]> {
  let ids: string[];
  try {
    ids = (await fs.list())
      .map((entry) => widgetIdFromPath(entry.path))
      .filter((id): id is string => id !== null)
      .sort();
  } catch {
    // A failed listing must not take Home down — it just means no auto-placement.
    return [];
  }

  const placed = new Set(dashboard.layout.map((p) => p.widget));
  const columns = dashboard.grid.columns;
  let y = dashboard.layout.reduce((lowest, p) => Math.max(lowest, p.y + p.h), 0);

  const placements: Placement[] = [];
  for (const id of ids) {
    if (placed.has(id)) continue;

    let raw: string;
    try {
      raw = (await fs.read(widgetPath(id))).content;
    } catch {
      continue;
    }

    const parsed = parseFile(raw);
    if (parsed.error) continue;

    // A malformed `homeSpan` shouldn't hide the widget — the full validation in
    // resolveWidget will report it in the tile where the author can see it.
    const pin = HomePinSchema.safeParse(parsed.data);
    if (!pin.success || !pin.data.showOnHome) continue;

    const w = Math.min(pin.data.homeSpan?.w ?? columns, columns);
    const h = pin.data.homeSpan?.h ?? DEFAULT_HOME_HEIGHT;
    placements.push({ widget: id, x: 0, y, w, h });
    y += h;
  }

  return placements;
}

async function resolveWidget(
  fs: VaultFS,
  placement: Placement,
  schemas: WidgetSchemaLookup,
): Promise<ResolvedWidget> {
  const path = widgetPath(placement.widget);
  const fail = (error: string, type?: string): ResolvedWidget => ({
    ok: false,
    id: placement.widget,
    ...(type ? { type } : {}),
    placement,
    path,
    error,
  });

  let raw: string;
  try {
    raw = (await fs.read(path)).content;
  } catch (err) {
    if (err instanceof VaultNotFoundError) return fail(`No such widget file: ${path}`);
    return fail(`Could not read ${path}: ${errorMessage(err)}`);
  }

  const parsed = parseFile(raw);
  if (parsed.error) return fail(`Invalid YAML: ${parsed.error}`);

  const probe = WidgetProbeSchema.safeParse(parsed.data);
  if (!probe.success) {
    return fail(`Missing or invalid "type" in frontmatter — ${formatIssues(probe.error)}`);
  }

  const type = probe.data.type;
  const schema = schemas.get(type);
  if (!schema) {
    return fail(`Unknown widget type "${type}". Known types: ${schemas.types().join(', ')}`, type);
  }

  // The id is implied by the filename, so configs don't have to repeat it.
  const withId = { id: placement.widget, ...parsed.data };
  const validated = schema.safeParse(withId);
  if (!validated.success) {
    const issues =
      validated.error instanceof z.ZodError ? formatIssues(validated.error) : String(validated.error);
    return fail(issues, type);
  }

  return { ok: true, id: placement.widget, type, placement, path, config: validated.data };
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
